import React, { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../helpers/axiosInstance";

const AddVehicle = () => {
    const fileInputRef = useRef(null);
    const [activeTab, setActiveTab] = useState("upload"); // default active tab
    const [selectedFile, setSelectedFile] = useState(null);
    const [truckNumber, setTruckNumber] = useState("");
    const [driverName, setDriverName] = useState("");
    const [driverMobile, setDriverMobile] = useState("");
    const [vendorCode, setVendorCode] = useState("");
    const [filteredVendors, setFilteredVendors] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [vendors, setVendors] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchVendors = async () => {
            try {
                const res = await axiosInstance.get("/api/vehicle/vendors");
                // Extract only vendor_code
                const codes = res.data.vendors.map(v => v.vendor_code);
                setVendors(codes);
            } catch (error) {
                console.error("Failed to fetch vendors:", error);
            }
        };
        fetchVendors();
    }, []);

    const handleFileUploadClick = () => {
        fileInputRef.current.click();
    };

    // Handle file selection
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setSelectedFile(file);
        console.log("Selected file:", file);
    };

    // Handle Upload button click
    const handleUpload = async () => {
        if (!selectedFile) return;

        try {
            const formData = new FormData();
            formData.append("file", selectedFile);

            // Call backend API
            const response = await axiosInstance.post("/api/vehicle/vehicle-upload", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            // Show success message from backend
            Swal.fire("Uploaded!", response.data.message, "success").then(() => {
                // Redirect to index page after closing the alert
                navigate("/vehicle_info"); // or your index route
            });

            // Reset file input
            setSelectedFile(null);
            fileInputRef.current.value = null;
        } catch (error) {
            console.error("Upload failed:", error);

            // Show error message
            Swal.fire(
                "Error!",
                error.response?.data?.error || "Failed to upload file",
                "error"
            );
        }
    };

    const handleDownloadFormat = () => {
        const dummyData = [
            { sl_no: "1", truck_number: "OD02FC9886", driver_name: "Ramesh", driver_mobile_number: "9876543210", vendor_code: "A129" },
            { sl_no: "2", truck_number: "MH04AK5567", driver_name: "Suresh", driver_mobile_number: "9123456780", vendor_code: "A201" },
        ];
        const worksheet = XLSX.utils.json_to_sheet(dummyData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "VehicleFormat");
        XLSX.writeFile(workbook, "Vehicle_Format.xlsx");
    };



    const handleVendorInput = (e) => {
        const value = e.target.value;
        setVendorCode(value);

        if (value.trim().length >= 2) { // show dropdown after 2 characters
            const filtered = vendors
                .filter((v) => v && v.toLowerCase().includes(value.trim().toLowerCase()));
            setFilteredVendors(filtered);
            setShowDropdown(true); // always true if input >= 2 chars
        } else {
            setShowDropdown(false);
            setFilteredVendors([]);
        }
    };

    const handleSelectVendor = (vendor) => {
        setVendorCode(vendor);
        setShowDropdown(false);
    };

    const handleAdd = async () => {
        // Basic frontend validation
        if (!truckNumber || !driverName || !driverMobile || !vendorCode) {
            Swal.fire("Error", "Please fill all fields", "error");
            return;
        }

        const vehicleData = {
            truck_number: truckNumber,
            driver_name: driverName,
            driver_mobile_number: driverMobile,
            vendor_code: vendorCode,
        };

        try {
            // Send POST request to backend
            const response = await axiosInstance.post("/api/vehicle/manual-entry", vehicleData);

            // Success message
            Swal.fire("Success", response.data.message || "Vehicle added successfully", "success").then(() => {
                // Redirect to index page after closing the alert
                navigate("/vehicle_info"); // or your index route
            });

            // Reset form
            setTruckNumber("");
            setDriverName("");
            setDriverMobile("");
            setVendorCode("");
            setShowDropdown(false);

        } catch (error) {
            console.error("Add vehicle error:", error);

            // Handle backend error messages
            let message = "Failed to add vehicle";
            if (error.response?.data?.error) {
                message = error.response.data.error;
            }
            Swal.fire("Error", message, "error");
        }
    };


    const handleCancel = () => {
        setTruckNumber("");
        setDriverName("");
        setDriverMobile("");
        setVendorCode("");
        setShowDropdown(false);
    };

    return (
        <div className="container position-relative">

            {/* Back button at top right */}
            <button
                className="btn btn-outline-secondary position-absolute"
                style={{ top: "25px", right: "20px", borderRadius: "20px", fontWeight: "500", zIndex: 20 }}
                onClick={() => navigate(-1)}
            >
                <i className="bi bi-arrow-left me-2"></i> Back
            </button>


            {/* Tabs / Navbar */}
            <ul className="nav nav-tabs justify-content-start mb-4 mt-5">
                <li className="nav-item">
                    <button
                        className={`nav-link ${activeTab === "upload" ? "active" : ""}`}
                        onClick={() => setActiveTab("upload")}
                    >
                        Upload File
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        className={`nav-link ${activeTab === "manual" ? "active" : ""}`}
                        onClick={() => setActiveTab("manual")}
                    >
                        Add Manually
                    </button>
                </li>
            </ul>

            {/* Tab Content */}
            <div>

                {/* Upload File Tab */}
                {activeTab === "upload" && (
                    <div
                        className="position-relative d-flex flex-column align-items-center justify-content-center p-4"
                        style={{
                            border: "2px dashed #ccc",
                            borderRadius: "10px",
                            width: "55%",
                            margin: "7% auto",
                            textAlign: "center",
                            backgroundColor: "#fafafa",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                            position: "relative",
                        }}
                    >
                        {/* ✅ Top right Download Format button */}
                        <button
                            className="btn btn-outline-primary position-absolute"
                            style={{ top: "15px", right: "20px", borderRadius: "20px", fontWeight: "500" }}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadFormat();
                            }}
                        >
                            <i className="bi bi-download me-2"></i> Download Format
                        </button>

                        <p className="h5 mb-2 mt-4 fw-bold" style={{ color: "#0A4595", fontWeight: "600" }}>
                            Select File Here
                        </p>

                        <p className="mb-4" style={{ color: "#6c757d", fontSize: "0.9rem" }}>
                            If you want to see which data is required, please download the format above.
                        </p>


                        {/* Hidden file input */}
                        <input
                            type="file"
                            accept=".csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            onChange={handleFileChange}
                        />

                        {/* Custom file box */}
                        <div className="d-flex align-items-center" style={{ width: "50%", minWidth: "220px", maxWidth: "250px" }}>
                            {/* File box */}
                            <div
                                className="flex-grow-1 p-1"
                                style={{ backgroundColor: "#d8d8d8ff", cursor: "pointer", border: "1px solid #9e9e9eff" }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleFileUploadClick();
                                }}
                            >
                                <span className="text-truncate">
                                    Choose File
                                </span>
                            </div>

                            {/* Span outside box on the right */}
                            <span
                                className="text-muted ms-2"
                                style={{ whiteSpace: "nowrap", fontSize: "0.9rem", alignSelf: "center" }}
                            >
                                {selectedFile ? selectedFile.name : "No file choosen"}
                            </span>
                        </div>

                        {/* File supported text with red star */}
                        <p
                            className="mt-2"
                            style={{ color: "#6c757d", fontSize: "0.9rem", fontStyle: "italic" }}
                        >
                            <span style={{ color: "red" }}>*</span> Files Supported: CSV, XLS, XLSX
                        </p>

                        {/* Upload button */}
                        <button
                            className="btn mt-3"
                            style={{
                                backgroundColor: "rgb(10, 69, 149)",
                                color: "white",
                                fontWeight: "500",
                                borderRadius: "6px",
                                width: "140px",
                            }}
                            onClick={handleUpload}
                            disabled={!selectedFile} // disabled until file is selected
                        >
                            Upload
                        </button>

                    </div>
                )}

                {/* Manual Entry Tab */}
                {activeTab === "manual" && (
                    <div className="bg-white p-4 border-0 shadow-sm mb-4 ms-5 me-5">
                        <h4 className="fw-bold text-center mb-4" style={{ color: "rgb(10, 69, 149)" }}>
                            Add Vehicle
                        </h4>

                        <div className="row g-4 mt-3">
                            <div className="col-md-6">
                                <label className="form-label fw-bold">Truck Number</label>
                                <input
                                    type="text"
                                    className="form-control rounded-pill"
                                    placeholder="Enter Truck Number"
                                    value={truckNumber}
                                    onChange={(e) => setTruckNumber(e.target.value)}
                                />
                            </div>

                            <div className="col-md-6">
                                <label className="form-label fw-bold">Driver Name</label>
                                <input
                                    type="text"
                                    className="form-control rounded-pill"
                                    placeholder="Enter Driver Name"
                                    value={driverName}
                                    onChange={(e) => setDriverName(e.target.value)}
                                />
                            </div>

                            <div className="col-md-6">
                                <label className="form-label fw-bold">Driver Mobile Number</label>
                                <input
                                    type="text"
                                    className="form-control rounded-pill"
                                    placeholder="Enter Mobile Number"
                                    value={driverMobile}
                                    onChange={(e) => setDriverMobile(e.target.value)}
                                    maxLength={10}
                                />
                            </div>

                            <div className="col-md-6 position-relative">
                                <label className="form-label fw-bold">Vendor Code</label>
                                <input
                                    type="text"
                                    className="form-control rounded-pill"
                                    placeholder="Enter Vendor Code"
                                    value={vendorCode}
                                    onChange={handleVendorInput}
                                    autoComplete="off"
                                />

                                {/* Dropdown suggestions */}
                                {showDropdown && (
                                    <ul
                                        className="list-group position-absolute w-100"
                                        style={{ zIndex: 10, maxHeight: "150px", overflowY: "auto", cursor: "pointer" }}
                                    >
                                        {filteredVendors.length > 0 ? (
                                            filteredVendors.map((v, idx) => (
                                                <li
                                                    key={idx}
                                                    className="list-group-item list-group-item-action border-0"
                                                    onClick={() => handleSelectVendor(v)}
                                                >
                                                    {v}
                                                </li>
                                            ))
                                        ) : (
                                            <li className="list-group-item border-0 text-muted" style={{ cursor: "default" }}>
                                                No vendors present
                                            </li>
                                        )}
                                    </ul>
                                )}

                            </div>

                        </div>

                        <div className="d-flex justify-content-center mt-5 gap-3">
                            <button
                                className="btn"
                                onClick={handleAdd}
                                style={{ backgroundColor: "rgb(10, 69, 149)", color: "white" }}
                            >
                                Add
                            </button>
                            <button className="btn btn-secondary rounded-pill px-4" onClick={handleCancel}>
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AddVehicle;
